import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from 'components/featured/Alert/Alert';
import Calendar from 'components/featured/Calendar/Calendar';
import DealInMotion from 'components/featured/Deals/components/DealInMotion/DealInMotion';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import ActivityTab from 'components/shared/CustomDashboard/ActivityTab/ActivityTab';
import AccountColumn from 'components/shared/CustomDashboard/Overview/AccountColumn';
import ActivityColumn from 'components/shared/CustomDashboard/Overview/Activity/ActivityColumn';
import ContactsColumn from 'components/shared/CustomDashboard/Overview/Contacts/ContactsColumn';
import DashboardDealCard from 'components/shared/CustomDashboard/Overview/DashboardDealCard';
import TaskColumn from 'components/shared/CustomDashboard/Overview/Tasks/TaskColumn';
import TodaysMeeting from 'components/shared/CustomDashboard/Overview/TodaysMeeting';
import CustomHomepage from 'components/shared/CustomHomePage/CustomHomepage';
import CustomLoader from 'components/shared/CustomLoader/CustomLoader';
import dayjs from 'dayjs';
import formatCurrency from 'helper/short-currency';
import { WidgetConfigItem, WidgetSize } from 'model/widget.model';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardService } from 'service/dashboard.service';
import { WidgetService } from 'service/widget.service';
import zeplinColor from 'theme/Color';
import { Constants } from 'utilities/constants';
import { Utilities } from 'utilities/utilities';
import { initialAlertState, useAlertStatus } from '../../../../customHook/useAlertStatus';
import AddWidgetModal from './AddWidgetModal';
import HomepageOverviewTab, { type ColumnConfig, type GridPosition } from './HomepageOverviewTab';

type SportsProps = {
  link: '';
  title: '';
  source: '';
};

export const Homepage = () => {
  const location = useLocation();
  const userName = sessionStorage.getItem('user-name') || '';
  const sf_redirect = sessionStorage.getItem('sf_redirect') || '';
  const currentTab = sessionStorage.getItem('currentTab') || '0';
  const redirectPath = sessionStorage.getItem('redirect_path') || '';
  const isSlackConnecting = sessionStorage.getItem('slackConnecting') === 'true';
  const slackAuthOrigin = sessionStorage.getItem('slackAuthOrigin') || 'activity';

  const navigate = useNavigate();

  const [value, setValue] = React.useState(0);
  const [alert, setAlert] = React.useState('');
  const [widgetModalOpen, setWidgetModalOpen] = React.useState(false);

  // ─── Widget alert (uses existing useAlertStatus hook) ──────────────────────
  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();
  const lastRemovedWidgetId = React.useRef<string | null>(null);
  const [widgetLoading, setWidgetLoading] = React.useState(false);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    sessionStorage.setItem('currentTab', newValue.toString());
    setValue(newValue);
  };

  const handleClose = () => {
    setAlert('');
  };

  const [data, setData] = React.useState<any>();
  const [sportsData, setSportsData] = React.useState<SportsProps[]>([
    {
      link: '',
      title: '',
      source: '',
    },
  ]);

  const getDeal = async () => {
    try {
      const res = await DashboardService.getDeal();

      setData(res);
    } catch (err: any) {
      setAlert(err?.message as string);
    }
  };
  const getSportNews = async () => {
    try {
      const res: { latestNews: SportsProps[] } = await DashboardService.getSportNews();

      setSportsData(res?.latestNews);
    } catch (err: any) {
      setAlert(`Something went wrong fetching news data`);
    }
  };
  React.useEffect(() => {
    getDeal();
    getSportNews();
    fetchWidgetConfig();

    if (
      isSlackConnecting &&
      slackAuthOrigin === 'communication' &&
      location.search &&
      redirectPath
    ) {
      navigate({ pathname: redirectPath, search: location.search }, { replace: true });
      return;
    }

    sf_redirect === 'Task' && setValue(2);
    sf_redirect === 'Overview' && setValue(0);
    sf_redirect === 'Activity' && setValue(1);
    redirectPath && !isSlackConnecting && navigate(redirectPath);
    if (!sf_redirect && !redirectPath) {
      setValue(+currentTab || 0);
    }
  }, []);

  // ─── Widget config from API ────────────────────────────────────────────────

  const [widgetConfig, setWidgetConfig] = React.useState<WidgetConfigItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  const fetchWidgetConfig = async () => {
    setWidgetLoading(true);
    try {
      const res = await WidgetService.getWidgetConfig();
      setWidgetConfig(res?.widgets ?? []);
    } catch (err: any) {
      console.error('Error fetching widget config', err);
    } finally {
      setWidgetLoading(false);
    }
  };

  // ─── Map widget id → render function ───────────────────────────────────────

  const widgetRenderers: Record<string, { render: () => React.ReactNode; colSpan?: number }> =
    React.useMemo(
      () => ({
        'recent-activity': {
          render: () => <ActivityColumn setActiveTab={setValue} setTabLoading={setLoading} />,
        },
        tasks: {
          render: () => (
            <TaskColumn setActiveTab={setValue} setTabLoading={setLoading} setValue={setValue} />
          ),
        },
        schedule: {
          render: () => <TodaysMeeting />,
        },
        alerts: {
          render: () => <Alert />,
        },
        'deals-in-motion': {
          colSpan: 2,
          render: () => (
            <DealInMotion
              setActiveTab={setValue}
              setTabLoading={setLoading}
              setValue={setValue}
              path='/clients'
            />
          ),
        },
        contacts: {
          render: () => <ContactsColumn />,
        },
        'actionable-accounts': {
          render: () => <AccountColumn setTabLoading={setLoading} path='/clients' />,
        },
      }),
      [setValue],
    );

  // ─── Default widgets (always shown regardless of enabled flag) ─────────────

  const DEFAULT_WIDGET_IDS = ['recent-activity', 'tasks', 'schedule', 'deals-in-motion', 'alerts'];

  // ─── Size → colSpan mapping (API size drives the grid) ──────────────────────

  const sizeToColSpan = (size: WidgetSize | undefined): number => {
    switch (size) {
      case 'full':
      case 'large':
        return 3;
      case 'medium':
        return 2;
      default:
        return 1;
    }
  };

  // ─── Build columns from widget config ──────────────────────────────────────

  const homepageColumns: ColumnConfig[] = React.useMemo(() => {
    return widgetConfig
      .filter((w) => {
        if (!widgetRenderers[w.id]) return false;
        // Default widgets always show; others only if enabled
        return DEFAULT_WIDGET_IDS.includes(w.id) || w.enabled;
      })
      .map((w) => ({
        id: w.id,
        position: { row: w.position[0], col: w.position[1] },
        // API size takes priority; fall back to hardcoded colSpan in widgetRenderers
        colSpan: sizeToColSpan(w.size) ?? widgetRenderers[w.id].colSpan ?? 1,
        render: widgetRenderers[w.id].render,
        removable: !DEFAULT_WIDGET_IDS.includes(w.id),
      }));
  }, [widgetConfig, widgetRenderers]);

  // ─── Position change → PATCH each moved widget ────────────────────────────

  const handlePositionChange = React.useCallback(
    async (positions: Record<string, GridPosition>) => {
      for (const [widgetId, pos] of Object.entries(positions)) {
        try {
          await WidgetService.updateWidgetConfig(widgetId, {
            position: [pos.row, pos.col],
          });
        } catch (err) {
          console.error(`Failed to update position for ${widgetId}`, err);
        }
      }
    },
    [],
  );

  // ─── Add widgets → PATCH enabled: true & refresh ──────────────────────────

  const handleAddWidgets = React.useCallback(async (widgetIds: string[]) => {
    try {
      await Promise.all(
        widgetIds.map((id) => WidgetService.updateWidgetConfig(id, { enabled: true })),
      );
      await fetchWidgetConfig();
      completeOperation('create', 'Widget added');
    } catch (err) {
      completeOperation('create', 'Failed to add widget', true);
    }
    setWidgetModalOpen(false);
  }, []);

  // ─── Remove widget → PATCH enabled: false & refresh ───────────────────────

  const handleRemoveWidget = React.useCallback(async (widgetId: string) => {
    setWidgetLoading(true);
    try {
      await WidgetService.updateWidgetConfig(widgetId, { enabled: false });
      await fetchWidgetConfig();
      lastRemovedWidgetId.current = widgetId;
      completeOperation('delete', 'Widget removed');
    } catch (err) {
      completeOperation('delete', 'Failed to remove widget', true);
    } finally {
      setWidgetLoading(false);
    }
  }, []);

  const handleUndoRemove = React.useCallback(async () => {
    const widgetId = lastRemovedWidgetId.current;
    if (!widgetId) return;
    setWidgetLoading(true);
    try {
      await WidgetService.updateWidgetConfig(widgetId, { enabled: true });
      await fetchWidgetConfig();
      lastRemovedWidgetId.current = null;
      setAlertState(initialAlertState);
    } catch (err) {
      completeOperation('delete', 'Failed to undo remove widget', true);
    } finally {
      setWidgetLoading(false);
    }
  }, []);

  // ─── Resize widget → PATCH size & reflow positions ─────────────────────────

  const handleResizeWidget = React.useCallback(
    async (
      widgetId: string,
      size: import('../../../../model/widget.model').WidgetSize,
      newSpan: number,
    ) => {
      try {
        await WidgetService.updateWidgetConfig(widgetId, { size });
      } catch (err) {
        console.error(`Failed to update size for ${widgetId}`, err);
      }
    },
    [],
  );

  const DashboardTabs = [
    {
      label: 'Overview',
      id: 1,
      isHidden: value !== 0,
      Component: (
        <Box>
          {homepageColumns.length > 0 ? (
            <HomepageOverviewTab
              columns={homepageColumns}
              onPositionChange={handlePositionChange}
              onRemoveWidget={handleRemoveWidget}
              onResizeWidget={handleResizeWidget}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                py: '80px',
                px: '24px',
                borderRadius: '14px',
                height: 'calc(100vh - 400px)',
                // border: `1px dashed ${zeplinColor.Neutral5}`,
              }}
            >
              <CustomLoader open={widgetLoading} />
            </Box>
          )}
        </Box>
      ),
    },
    {
      label: 'Activity',
      id: 2,
      isHidden: value !== 1,
      Component: (
        <Box>
          <ActivityTab />
        </Box>
      ),
    },

    {
      label: 'Calendar',
      id: 3,
      isHidden: value !== 2,
      Component: (
        <Box>
          <Calendar />
        </Box>
      ),
    },
  ];
  return (
    <>
      <CustomHomepage
        sportsData={sportsData}
        ProfileDetails={
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '24px', xl: '36px' },
                fontWeight: 300,
                color: zeplinColor.Neutral1,
              }}
            >
              Hello, {userName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src='/assets/calendar-fade.svg' width={18} height={18} alt='calendar fade' />
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral6}>
                {Utilities.formatDate(dayjs())}
              </Typography>
            </Box>
          </Box>
        }
        DealCard={
          <Box sx={{ display: 'flex', gap: '16px', flex: 1 }}>
            <DashboardDealCard
              color={Constants.DealsColor.totalDealsWon}
              val={
                +data?.totalDealsWon >= 0
                  ? `$${formatCurrency(data?.totalDealsWon) ?? 0}`
                  : undefined
              }
              percent={data?.dealsWonDelta || 0}
              text={Constants.DealsText.totalDealsWon}
            />

            <DashboardDealCard
              color={Constants.DealsColor.totalOpenDeals}
              val={
                +data?.totalOpenDeals >= 0
                  ? `$${formatCurrency(data?.totalOpenDeals) ?? 0}`
                  : undefined
              }
              percent={data?.openDealsDelta || 0}
              text={Constants.DealsText.totalOpenDeals}
            />

            <DashboardDealCard
              color={Constants.DealsColor.quaterly}
              val={
                +data?.quarterRevenue >= 0
                  ? `$${formatCurrency(data?.quarterRevenue) ?? 0}`
                  : undefined
              }
              percent={data?.dealsWonDelta || 0}
              text={Constants.DealsText.quaterly}
            />

            <DashboardDealCard
              color={Constants.DealsColor.totalTasks}
              val={data?.totalTasks}
              text={Constants.DealsText.totalTasks}
            />
          </Box>
        }
        DashboardTabs={DashboardTabs}
        CTA={
          value === 0 ? (
            <CustomButton
              disabled={false}
              variant='outlined'
              color='customSecondary'
              style={{
                color: zeplinColor.BasicBlack,
                // width: '100%',
                margin: 'auto',
                borderRadius: '14px',
                height: 40,
                borderWidth: 1,
                borderColor: zeplinColor.Neutral9,
                display: 'flex',
                gap: '16px',
                '&:hover': {
                  backgroundColor: zeplinColor.Neutral5,
                },
              }}
              text={'Add widget'}
              iconPosition='start'
              icon={<img src={'/assets/widget-icon.svg'} />}
              onClick={() => setWidgetModalOpen(true)}
              // tabindex={5}
            />
          ) : (
            <></>
          )
        }
        handleChange={handleChange}
        value={value}
        alert={alert}
        handleClose={handleClose}
      />
      <AddWidgetModal
        open={widgetModalOpen}
        onClose={() => setWidgetModalOpen(false)}
        onAdd={handleAddWidgets}
        enabledWidgets={widgetConfig
          .filter((w) => w.enabled || DEFAULT_WIDGET_IDS.includes(w.id))
          .map((w) => w.id)}
      />

      {/* ─── Widget added / removed toast ──────────────────────────────────── */}
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {getAlertContent(alertState)}
            {alertState.operation === 'delete' && lastRemovedWidgetId.current && (
              <CustomButton
                disabled={false}
                variant='text'
                onClick={handleUndoRemove}
                text='Undo'
                style={{ backgroundColor: zeplinColor.Brand1 }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                buttonVariant='buttonMedium'
              />
            )}
          </Box>
        }
        error={alertState.status === 'error'}
      />
      <CustomLoader open={widgetLoading} />
    </>
  );
};

export default Homepage;
